import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import {
  IMyProfileFormModel,
  myProfileFormDefaultValues,
  myProfileFormSchema,
} from "../models/myProfileFormModel";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { IUserModel } from "../../../../common/infrastructure/hooks/useAuthentication";
import { normalizePhone } from "../../../../common/components/forms/inputs/phoneField/utils/PhoneInputUtility";
import { useAppContext } from "../../../../AppContext";

interface IUseMyProfileFormProps {
  profile: IUserModel | undefined;
  onSuccess: () => void;
}

export const useMyProfileForm = ({ profile, onSuccess }: IUseMyProfileFormProps) => {
  const { currentUser } = useAppContext();
  // In a profile a global admin opened from the admin panel, the phone and email (how the user
  // signs in and resets the password) stay as they are; the API refuses to change them there.
  const isSignInLocked = !!currentUser?.impersonatedBy;

  const form = useForm<IMyProfileFormModel>({
    resolver: yupResolver(myProfileFormSchema),
    defaultValues: myProfileFormDefaultValues,
  });

  useEffect(() => {
    if (profile) {
      form.reset({
        firstName: profile.firstName ?? "",
        lastName: profile.lastName ?? "",
        phoneNumber: profile.phoneNumber ?? "",
        email: profile.email ?? "",
        birthDate: profile.birthDate ? new Date(profile.birthDate) : null,
        fontSizePercent: profile.fontSizePercent ?? 100,
      });
    }
  }, [profile]);

  const { mutate, isLoading } = useApiMutation({
    onSuccess: () => {
      onSuccess();
    },
  });

  const onSubmit = form.handleSubmit((data) => {
    const normalizedPhone = normalizePhone(data.phoneNumber ?? null);

    mutate({
      endpoint: endpointsMetadata.authentication.updateMyProfile,
      method: "put",
      payload: {
        firstName: data.firstName,
        lastName: data.lastName || null,
        phoneNumber: normalizedPhone || null,
        email: data.email?.trim() || null,
        birthDate: data.birthDate ? data.birthDate.toISOString() : null,
        fontSizePercent: data.fontSizePercent,
      },
      successMessage: "Профилът е обновен успешно.",
      invalidateKeys: [[endpointsMetadata.authentication.getUserProfile]],
    });
  });

  return {
    form,
    onSubmit,
    isSaving: isLoading,
    isSignInLocked,
  };
};
